import { SubmitHandler, useForm } from "react-hook-form";
import { useState } from "react";
import { addFood } from "../../../../../store/food/actions/add";
import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  IconButton,
  Image,
  Input,
  Link,
  Radio,
  RadioGroup,
  Text,
  Textarea,
  Wrap,
} from "@chakra-ui/react";
import { toggleFoodVisibility } from "../../../../../store/food/actions/toggleVisibility";
import { DeleteIcon } from "@chakra-ui/icons";
import { deleteFood } from "../../../../../store/food/actions/delete";
import { TalesSpinner } from "../../../../atoms";
import { FileUploader } from "../../../../atoms/FileUploader";
import { useDispatch, useSelector } from "react-redux";
import { AppThunkDispatch } from "../../../../../store";
import {
  getFoodData,
  getFoodError,
  isFoodLoading,
} from "../../../../../store/food/selectors";
import useFoods from "../../../../../utils/hooks/useFood";

interface FoodInput {
  title: string;
  show: boolean;
  description: string;
  website?: string;
}
export default function Foods() {
  useFoods();
  const dispatch = useDispatch<AppThunkDispatch>();
  const isPending = useSelector(isFoodLoading);
  const foods = useSelector(getFoodData);
  const error = useSelector(getFoodError);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    getValues,
  } = useForm<FoodInput>({
    defaultValues: {
      title: "",
      description: "",
      show: true,
    },
  });
  const [file, setFile] = useState<File>();

  const onSubmit: SubmitHandler<FoodInput> = (data) => {
    const newFood = {
      title: getValues("title"),
      description: getValues("description"),
      website: getValues("website"),
      show: getValues("show"),
      file: file,
    };
    dispatch(addFood(newFood));
  };

  return (
    <>
      {error && (
        <Box>
          <Text my="10px">{error}</Text>
        </Box>
      )}
      <Wrap>
        {foods &&
          foods.map((food) => (
            <Card key={food.logoUrl} w="xs" m="10px">
              <CardHeader>
                <Heading size="base">{food.title}</Heading>
              </CardHeader>
              <CardBody>
                <Text whiteSpace="pre-line">{food.description}</Text>
                {food.website && (
                  <Link href={food.website}>{food.website}</Link>
                )}
                <Image src={food.logoUrl} maxH="80px" maxW="100%" objectFit="contain" alt={food.title} />
              </CardBody>
              <CardFooter>
                <Button
                  aria-label={food.show ? "Nascondi" : "Mostra"}
                  variant="solid"
                  colorScheme={food.show ? "gray" : "green"}
                  onClick={() => dispatch(toggleFoodVisibility(food))}
                  marginRight="20px"
                >
                  {food.show ? "NASCONDI DAL SITO" : "MOSTRA SUL SITO"}
                </Button>
                <IconButton
                  aria-label="Elimina"
                  variant="solid"
                  icon={<DeleteIcon />}
                  colorScheme="red"
                  onClick={() => dispatch(deleteFood(food))}
                />
              </CardFooter>
            </Card>
          ))}
        <Card w="xs" m="10px">
          {isPending && (
            <CardBody>
              <Box w="30px" m="auto">
                <TalesSpinner />
              </Box>
            </CardBody>
          )}
          {!isPending && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardHeader>
                <Heading size="base">Aggiungi nuovo</Heading>
              </CardHeader>
              <CardBody>
                <FormControl
                  marginBottom="20px"
                  isInvalid={errors.title != null}
                  isRequired={true}
                >
                  <FormLabel htmlFor="title">Nome</FormLabel>
                  <Input
                    id="title"
                    type="text"
                    {...register("title", { required: "Campo richiesto" })}
                  />
                  <FormErrorMessage>
                    {errors.title && errors.title.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl
                  marginBottom="20px"
                  isRequired={true}
                  isInvalid={errors.description != null}
                >
                  <FormLabel htmlFor="description">Descrizione</FormLabel>
                  <Textarea
                    whiteSpace="pre-wrap"
                    id="description"
                    {...register("description")}
                  />
                  <FormErrorMessage>
                    {errors.description && errors.description.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl marginBottom="20px">
                  <FormLabel htmlFor="website">Sito</FormLabel>
                  <Input id="website" type="text" {...register("website")} />
                  <FormErrorMessage>
                    {errors.title && errors.title.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl marginBottom="20px">
                  <FormLabel>Immagine</FormLabel>
                  <FileUploader handleFile={(file) => setFile(file)} />
                </FormControl>
                <FormControl>
                  <FormLabel>Mostrare sul sito</FormLabel>
                  <RadioGroup defaultValue={getValues("show").toString()}>
                    <Flex>
                      <Radio
                        value="true"
                        marginRight="30px"
                        {...register("show")}
                      >
                        Sì
                      </Radio>
                      <Radio value="false" {...register("show")}>
                        No
                      </Radio>
                    </Flex>
                  </RadioGroup>
                </FormControl>
              </CardBody>
              <CardFooter>
                <Button
                  type="submit"
                  isDisabled={
                    isPending ||
                    watch("title") === "" ||
                    watch("description") === "" ||
                    !watch("description") ||
                    !watch("title") ||
                    foods?.some((food) => food.title === watch("title"))
                  }
                  colorScheme="green"
                >
                  Crea
                </Button>
              </CardFooter>
            </form>
          )}
        </Card>
      </Wrap>
    </>
  );
}
